import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { ChatPresenceUser } from '../../../../shared/models/chat/chat-presence-user.model';
import { ChatMessageCreatedEvent } from '../../../../shared/models/chat/chat-message-created-event.model';
import { ChatMessageDeletedEvent } from '../../../../shared/models/chat/chat-message-deleted-event.model';
import { ChatParticipantAddedEvent } from '../../../../shared/models/chat/chat-participant-added-event.model';
import { ChatParticipantRemovedEvent } from '../../../../shared/models/chat/chat-participant-removed-event.model';
import { ChatParticipantRoleUpdatedEvent } from '../../../../shared/models/chat/chat-participant-role-updated-event.model';
import { ChatConversationUpdatedEvent } from '../../../../shared/models/chat/chat-conversation-updated-event.model';
import { ChatMessagesReadEvent } from '../../../../shared/models/chat/chat-messages-read-event.model';
import { ChatConversationCreatedEvent } from '../../../../shared/models/chat/chat-conversation-created-event.model';
import { ChatTypingPayload } from '../../../../shared/models/chat/chat-typing-payload.model';
import { environment } from '../../../../../environments/environment';
import { ChatMessageUpdatedEvent } from '../../../../shared/models/chat/chat-message-updated-event.model';



@Injectable({
  providedIn: 'root',
})
export class ChatRealtimeService {

  private echo: Echo<'reverb'> | null = null;

  /*
  |--------------------------------------------------------------------------
  | Control de canales activos
  |--------------------------------------------------------------------------
  |
  | Evita suscribirse varias veces al mismo canal y recibir eventos
  | duplicados.
  |
  */

  private readonly subscribedConversations = new Set<number>();
  private subscribedUserId: number | null = null;
  private subscribedRootAgencyId: number | null = null;

  /*
  |--------------------------------------------------------------------------
  | Usuarios conectados
  |--------------------------------------------------------------------------
  */

  private readonly onlineUsers = new Map<number, ChatPresenceUser>();

  /*
  |--------------------------------------------------------------------------
  | Subjects internos
  |--------------------------------------------------------------------------
  */

  private readonly messageCreatedSubject =
    new Subject<ChatMessageCreatedEvent>();

  private readonly messageDeletedSubject =
    new Subject<ChatMessageDeletedEvent>();

  private readonly messageUpdatedSubject =
    new Subject<ChatMessageUpdatedEvent>();

  private readonly participantAddedSubject =
    new Subject<ChatParticipantAddedEvent>();

  private readonly participantRemovedSubject =
    new Subject<ChatParticipantRemovedEvent>();

  private readonly participantRoleUpdatedSubject =
    new Subject<ChatParticipantRoleUpdatedEvent>();

  private readonly conversationUpdatedSubject =
    new Subject<ChatConversationUpdatedEvent>();

  private readonly messagesReadSubject =
    new Subject<ChatMessagesReadEvent>();

  private readonly conversationCreatedSubject =
    new Subject<ChatConversationCreatedEvent>();

  private readonly typingSubject =
    new Subject<ChatTypingPayload>();

  private readonly presenceUsersSubject =
    new Subject<ChatPresenceUser[]>();



  /*
  |--------------------------------------------------------------------------
  | Observables públicos
  |--------------------------------------------------------------------------
  */

  readonly messageCreated$: Observable<ChatMessageCreatedEvent> =
    this.messageCreatedSubject.asObservable();

  readonly messageDeleted$: Observable<ChatMessageDeletedEvent> =
    this.messageDeletedSubject.asObservable();

  readonly messageUpdated$: Observable<ChatMessageUpdatedEvent> =
    this.messageUpdatedSubject.asObservable();

  readonly participantAdded$: Observable<ChatParticipantAddedEvent> =
    this.participantAddedSubject.asObservable();

  readonly participantRemoved$: Observable<ChatParticipantRemovedEvent> =
    this.participantRemovedSubject.asObservable();

  readonly participantRoleUpdated$: Observable<ChatParticipantRoleUpdatedEvent> =
    this.participantRoleUpdatedSubject.asObservable();

  readonly conversationUpdated$: Observable<ChatConversationUpdatedEvent> =
    this.conversationUpdatedSubject.asObservable();

  readonly messagesRead$: Observable<ChatMessagesReadEvent> =
    this.messagesReadSubject.asObservable();

  readonly conversationCreated$: Observable<ChatConversationCreatedEvent> =
    this.conversationCreatedSubject.asObservable();

  readonly typing$: Observable<ChatTypingPayload> =
    this.typingSubject.asObservable();

  readonly presenceUsers$: Observable<ChatPresenceUser[]> =
    this.presenceUsersSubject.asObservable();

  /*
  |--------------------------------------------------------------------------
  | Conectar Angular con Laravel Reverb
  |--------------------------------------------------------------------------
  */

  connect(): void {
    if (this.echo !== null) {
      return;
    }

    const token = this.getToken();

    if (!token) {
      console.warn(
        'No se pudo conectar a Reverb porque no existe un token de autenticación.'
      );

      return;
    }

    /*
     * Laravel Echo utiliza pusher-js como cliente del protocolo WebSocket
     * compatible con Laravel Reverb.
     */
    (window as any).Pusher = Pusher;

    this.echo = new Echo({
      broadcaster: 'reverb',

      key: environment.reverb.key,

      wsHost: environment.reverb.host,
      wsPort: environment.reverb.port,
      wssPort: environment.reverb.port,

      forceTLS: environment.reverb.scheme === 'https',

      enabledTransports: [
        'ws',
        'wss',
      ],

      /*
       * Verifica la ruta real en Laravel con:
       *
       * php artisan route:list --path=broadcasting
       */
      // authEndpoint: `${environment.apiUrl}/broadcasting/auth`,
      
        authEndpoint: environment.broadcastingAuthUrl,
        
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    });
  }

  /*
  |--------------------------------------------------------------------------
  | Estado de conexión
  |--------------------------------------------------------------------------
  */

  isConnected(): boolean {
    return this.echo !== null;
  }

  /*
  |--------------------------------------------------------------------------
  | Canal privado personal
  |--------------------------------------------------------------------------
  |
  | Permite recibir conversaciones nuevas aunque Angular todavía no esté
  | suscrito al canal privado de esa conversación.
  |
  */

  subscribeToUserChannel(userId: number): void {
    const echo = this.requireEcho();

    if (this.subscribedUserId === userId) {
      return;
    }

    if (this.subscribedUserId !== null) {
      this.leaveUserChannel(this.subscribedUserId);
    }

    echo
      .private(`App.Models.User.${userId}`)
      .listen(
        '.chat.conversation.created',
        (event: ChatConversationCreatedEvent) => {
          this.conversationCreatedSubject.next(event);
        }
      );

    this.subscribedUserId = userId;
  }

  leaveUserChannel(userId: number): void {
    this.echo?.leave(`App.Models.User.${userId}`);

    if (this.subscribedUserId === userId) {
      this.subscribedUserId = null;
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Canal privado de conversación
  |--------------------------------------------------------------------------
  */

  subscribeToConversation(conversationId: number): void {
    const echo = this.requireEcho();

    if (this.subscribedConversations.has(conversationId)) {
      return;
    }

    const channel = echo.private(
      `chat.conversation.${conversationId}`
    );

    channel.listen(
      '.chat.message.created',
      (event: ChatMessageCreatedEvent) => {
        this.messageCreatedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.message.updated',
      (event: ChatMessageUpdatedEvent) => {
        this.messageUpdatedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.message.deleted',
      (event: ChatMessageDeletedEvent) => {
        this.messageDeletedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.participant.added',
      (event: ChatParticipantAddedEvent) => {
        this.participantAddedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.participant.removed',
      (event: ChatParticipantRemovedEvent) => {
        this.participantRemovedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.participant.role.updated',
      (event: ChatParticipantRoleUpdatedEvent) => {
        this.participantRoleUpdatedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.conversation.updated',
      (event: ChatConversationUpdatedEvent) => {
        this.conversationUpdatedSubject.next(event);
      }
    );

    channel.listen(
      '.chat.messages.read',
      (event: ChatMessagesReadEvent) => {
        this.messagesReadSubject.next(event);
      }
    );

    /*
     * El whisper de typing viaja directamente entre los clientes
     * conectados al canal. No utiliza endpoint ni evento PHP.
     */
    // channel.listenForWhisper(
    //   'typing',
    //   (payload: ChatTypingPayload) => {
    //     this.typingSubject.next(payload);
    //   }
    // );

    channel.listenForWhisper(
      'typing',
      (
        payload: Omit<
          ChatTypingPayload,
          'conversation_id'
        >
      ) => {
        this.typingSubject.next({
          ...payload,
          conversation_id: conversationId,
        });
      }
    );

    this.subscribedConversations.add(conversationId);
  }

  leaveConversation(conversationId: number): void {
    this.echo?.leave(
      `chat.conversation.${conversationId}`
    );

    this.subscribedConversations.delete(conversationId);
  }

  leaveAllConversations(): void {
    const conversationIds = Array.from(
      this.subscribedConversations
    );

    for (const conversationId of conversationIds) {
      this.leaveConversation(conversationId);
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Indicador "está escribiendo"
  |--------------------------------------------------------------------------
  */

  // whisperTyping(
  //   conversationId: number,
  //   payload: ChatTypingPayload
  // ): void {
  //   const echo = this.requireEcho();

  //   echo
  //     .private(`chat.conversation.${conversationId}`)
  //     .whisper('typing', payload);
  // }

  // cambia whisperTyping() para que no exija enviar el conversation_id dentro del payload:
  whisperTyping(
    conversationId: number,
    payload: Omit<
      ChatTypingPayload,
      'conversation_id'
    >
  ): void {
    const echo = this.requireEcho();

    echo
      .private(
        `chat.conversation.${conversationId}`
      )
      .whisper('typing', payload);
  }

  /*
  |--------------------------------------------------------------------------
  | Canal de presencia
  |--------------------------------------------------------------------------
  |
  | here(): lista inicial de usuarios conectados.
  | joining(): usuario que acaba de conectarse.
  | leaving(): usuario que acaba de desconectarse.
  |
  */

  joinPresenceChannel(rootAgencyId: number): void {
    const echo = this.requireEcho();

    if (this.subscribedRootAgencyId === rootAgencyId) {
      return;
    }

    if (this.subscribedRootAgencyId !== null) {
      this.leavePresenceChannel(
        this.subscribedRootAgencyId
      );
    }

    echo
      .join(`chat.users.${rootAgencyId}`)

      .here((users: ChatPresenceUser[]) => {
        this.onlineUsers.clear();

        for (const user of users) {
          this.onlineUsers.set(user.id, user);
        }

        this.emitOnlineUsers();
      })

      .joining((user: ChatPresenceUser) => {
        this.onlineUsers.set(user.id, user);
        this.emitOnlineUsers();
      })

      .leaving((user: ChatPresenceUser) => {
        this.onlineUsers.delete(user.id);
        this.emitOnlineUsers();
      })

      .error((error: unknown) => {
        console.error(
          'Error al entrar al canal de presencia:',
          error
        );
      });

    this.subscribedRootAgencyId = rootAgencyId;
  }

  leavePresenceChannel(rootAgencyId: number): void {
    this.echo?.leave(
      `chat.users.${rootAgencyId}`
    );

    if (this.subscribedRootAgencyId === rootAgencyId) {
      this.subscribedRootAgencyId = null;
    }

    this.onlineUsers.clear();
    this.emitOnlineUsers();
  }

  /*
  |--------------------------------------------------------------------------
  | Consultar usuarios conectados
  |--------------------------------------------------------------------------
  */

  isUserOnline(userId: number): boolean {
    return this.onlineUsers.has(userId);
  }

  getOnlineUsers(): ChatPresenceUser[] {
    return Array.from(
      this.onlineUsers.values()
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Desconectar Echo completamente
  |--------------------------------------------------------------------------
  |
  | Debe utilizarse al cerrar sesión.
  |
  */

  disconnect(): void {
    this.echo?.disconnect();
    this.echo = null;

    this.subscribedConversations.clear();
    this.subscribedUserId = null;
    this.subscribedRootAgencyId = null;

    this.onlineUsers.clear();
    this.emitOnlineUsers();
  }

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  private requireEcho(): Echo<'reverb'> {
    if (this.echo === null) {
      throw new Error(
        'ChatRealtimeService no está conectado. Ejecuta connect() primero.'
      );
    }

    return this.echo;
  }

  private emitOnlineUsers(): void {
    this.presenceUsersSubject.next(
      Array.from(this.onlineUsers.values())
    );
  }

  private getToken(): string {
    return (
      localStorage.getItem('token')
      || sessionStorage.getItem('token')
      || ''
    );
  }
}